import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

const DEFAULT_POLL_MS = 60_000;
const DEBOUNCE_MS = 300;

const ENDPOINTS = {
  hyperliquid: '/api/hyperliquid/liquidation-map',
  dydx: '/api/dydx/liquidation-map',
  synthetic: '/api/synthetic/liquidation-map',
  combined: '/api/liquidation-map/combined',
};

function createLiqMapHook(defaultEndpoint) {
  return function useLiqMap({
    symbol,
    bucketCount = 100,
    priceRangePct = 0.5,
    pollIntervalMs = DEFAULT_POLL_MS,
    endpoint = defaultEndpoint,
  } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Adjusting state during render so the consumer never paints the new
    // symbol/params alongside the previous request's data/error/loading.
    // Without this, the analytics effect keyed on [error, symbol] would fire
    // liqmap.error_shown with the new symbol and the prior symbol's error
    // before any post-commit effect could clear it. React discards this
    // render and immediately re-renders with the cleared values.
    const [paramsKey, setParamsKey] = useState(
      () => `${endpoint}|${symbol}|${bucketCount}|${priceRangePct}`
    );
    const currentParamsKey = `${endpoint}|${symbol}|${bucketCount}|${priceRangePct}`;
    if (paramsKey !== currentParamsKey) {
      setParamsKey(currentParamsKey);
      setData(null);
      setError(null);
      setLoading(true);
    }

    const abortRef = useRef(null);
    const intervalRef = useRef(null);
    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);

    const fetchData = useCallback(async ({ background = false } = {}) => {
      if (!symbol) {
        setLoading(false);
        return;
      }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        // Skip work on hidden tabs; clear loading so consumer doesn't spin forever.
        setLoading(false);
        return;
      }

      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;
      const id = ++requestIdRef.current;
      // Foreground fetches (symbol/param change) surface the loading overlay.
      // Background poll refreshes leave the previous data on-screen so users
      // don't see a flash every poll interval.
      if (!background) {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams({
          symbol,
          bucketCount: String(bucketCount),
          priceRangePct: String(priceRangePct),
        });
        const url = `${API_BASE_URL}${endpoint}?${params.toString()}`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) {
          // Surface the server-provided error message (e.g. "Price unavailable
          // for symbol", "symbol not supported") rather than a bare HTTP code.
          let serverError = null;
          try {
            const body = await res.json();
            if (body && typeof body.error === 'string') serverError = body.error;
          } catch {
            // body wasn't JSON; fall through to HTTP code
          }
          throw new Error(serverError || `HTTP ${res.status}`);
        }
        const json = await res.json();
        if (requestIdRef.current !== id) return;
        setData(json);
        setError(null);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        if (requestIdRef.current !== id) return;
        console.error('[liqMap]', err);
        setError(err?.message || 'Failed to load liquidation map');
      } finally {
        // Clear loading only when this request is still the latest AND it
        // wasn't aborted. The aborted-check matters for symbol/param changes:
        // the effect cleanup aborts the in-flight fetch *before* a replacement
        // fetchData runs (debounced 300ms later), so requestIdRef hasn't been
        // bumped yet — without the abort check, the aborted finally would flip
        // loading back to false during the debounce window for the new symbol.
        // The replacement fetchData will set loading=true again when it starts.
        if (!controller.signal.aborted && requestIdRef.current === id) {
          setLoading(false);
        }
      }
    }, [endpoint, symbol, bucketCount, priceRangePct]);

    // Debounced refetch on symbol / param change. Rapid changes coalesce into one fetch.
    // State-clearing for symbol/param changes happens synchronously above
    // (adjust-state-during-render), so this effect only schedules the debounced
    // fetch and tears down on cleanup.
    useEffect(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        fetchData();
      }, DEBOUNCE_MS);
      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        if (abortRef.current) {
          abortRef.current.abort();
          abortRef.current = null;
        }
      };
    }, [fetchData]);

    // Polling with visibility-pause: hidden tabs don't waste fetches.
    // Poll fetches are background — they refresh data silently without
    // flashing the loading overlay every interval.
    useEffect(() => {
      const installInterval = () => {
        if (intervalRef.current != null) return;
        intervalRef.current = setInterval(() => {
          fetchData({ background: true });
        }, pollIntervalMs);
      };
      const clearPoll = () => {
        if (intervalRef.current != null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
      const onVisibility = () => {
        if (typeof document === 'undefined') return;
        if (document.visibilityState === 'hidden') {
          clearPoll();
        } else if (intervalRef.current == null) {
          fetchData({ background: true });
          installInterval();
        }
      };

      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        installInterval();
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility);
      }

      return () => {
        clearPoll();
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibility);
        }
      };
    }, [fetchData, pollIntervalMs]);

    return { data, loading, error };
  };
}

// Backward-compatible default hook (hyperliquid endpoint).
// Accepts `endpoint` param override so callers can point at any source.
export const useLiquidationMap = createLiqMapHook(ENDPOINTS.hyperliquid);

// Named per-source hooks. Each one is a thin wrapper that accepts the same
// option shape as the symbol-only positional convenience signature.
// Callers can pass either:
//   useHyperliquidLiqMap('BTC')                       // string symbol
//   useHyperliquidLiqMap({ symbol: 'BTC', bucketCount: 50 })
function makeNamedHook(endpoint) {
  const inner = createLiqMapHook(endpoint);
  return function useNamedLiqMap(arg) {
    const opts = typeof arg === 'string' ? { symbol: arg } : (arg || {});
    return inner({ ...opts, endpoint });
  };
}

export const useHyperliquidLiqMap = makeNamedHook(ENDPOINTS.hyperliquid);
export const useDydxLiqMap = makeNamedHook(ENDPOINTS.dydx);
export const useSyntheticLiqMap = makeNamedHook(ENDPOINTS.synthetic);

// Combined endpoint returns `{ sources, combined: { bars, cumulative, totalUsd } }`
// rather than top-level bars/cumulative. The chart component reads `data.bars`
// directly, so flatten combined.* up to the top level here. Preserves the
// `sources` block for callers that want per-source detail.
const useCombinedLiqMapRaw = makeNamedHook(ENDPOINTS.combined);
export function useCombinedLiqMap(arg) {
  const raw = useCombinedLiqMapRaw(arg);
  const data = useMemo(() => {
    if (!raw.data || !raw.data.combined) return raw.data;
    return {
      ...raw.data,
      bars: raw.data.combined.bars,
      cumulative: raw.data.combined.cumulative,
      totalUsd: raw.data.combined.totalUsd,
    };
  }, [raw.data]);
  return { data, loading: raw.loading, error: raw.error };
}

export const LIQ_MAP_ENDPOINTS = ENDPOINTS;

export function prepareChartSeries(data) {
  if (!data || !Array.isArray(data.bars) || data.bars.length === 0) {
    return {
      longBars: [],
      shortBars: [],
      cumLong: [],
      cumShort: [],
      prices: [],
    };
  }
  const longBars = [];
  const shortBars = [];
  const cumLong = [];
  const cumShort = [];
  const prices = [];
  for (let i = 0; i < data.bars.length; i += 1) {
    const bar = data.bars[i];
    longBars.push({ time: i, value: Number(bar.longSize) || 0 });
    shortBars.push({ time: i, value: Number(bar.shortSize) || 0 });
    cumLong.push({ time: i, value: Number(data.cumulative?.long?.[i]) || 0 });
    cumShort.push({ time: i, value: Number(data.cumulative?.short?.[i]) || 0 });
    prices.push(bar.price);
  }
  return { longBars, shortBars, cumLong, cumShort, prices };
}

export default useLiquidationMap;
